'use client';

import { useState } from 'react';

export interface AgentPersona {
  name: string;
  gender: 'female' | 'male';
  description: string;
  agentIdEnvKey: string;
}

export const AGENT_PERSONAS: AgentPersona[] = [
  {
    name: 'Sophie',
    gender: 'female',
    description: 'Warm & friendly',
    agentIdEnvKey: 'NEXT_PUBLIC_ELEVENLABS_AGENT_ID_SOPHIE',
  },
  {
    name: 'James',
    gender: 'male',
    description: 'Calm & professional',
    agentIdEnvKey: 'NEXT_PUBLIC_ELEVENLABS_AGENT_ID_JAMES',
  },
];

interface Props {
  onChooseType: () => void;
  onChooseVoice: (persona: AgentPersona) => void;
}

export default function VoiceAgentPicker({ onChooseType, onChooseVoice }: Props) {
  const [showAgents, setShowAgents] = useState(false);

  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-black/10 bg-white/95 backdrop-blur">
      {/* Brand header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#1a4a3a]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <span className="text-xs font-semibold tracking-widest text-[#7a7870] uppercase">Smart Property Search</span>
        </div>
        <h1 className="font-serif text-3xl font-semibold leading-snug text-[#0f0f0d]">
          Find your perfect<br />property in the UK
        </h1>
        <p className="mt-1.5 text-sm text-[#7a7870]">How would you like to search?</p>
      </div>

      {/* Choice buttons */}
      <div className="px-6 pb-4 flex flex-col gap-3">
        {/* Talk to an Agent */}
        <button
          type="button"
          onClick={() => setShowAgents(!showAgents)}
          className={`group flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition ${
            showAgents
              ? 'border-[#2d7a5f] bg-[#d4ede5]/45'
              : 'border-black/10 bg-white hover:border-[#85d4b4] hover:bg-[#d4ede5]/30'
          }`}
        >
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
            showAgents ? 'bg-[#1a4a3a]' : 'bg-[#f3f0ea] group-hover:bg-[#d4ede5]'
          }`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${showAgents ? 'text-white' : 'text-[#7a7870] group-hover:text-[#1a4a3a]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          </div>
          <div>
            <p className={`text-sm font-semibold ${showAgents ? 'text-[#1a4a3a]' : 'text-[#0f0f0d]'}`}>Talk to an Agent</p>
            <p className="text-xs text-[#7a7870]">Have a voice conversation with our AI</p>
          </div>
        </button>

        {/* Agent selection (expanded) */}
        {showAgents && (
          <div className="flex gap-2 px-1 animate-in fade-in slide-in-from-top-2 duration-200">
            {AGENT_PERSONAS.map((persona) => (
              <button
                key={persona.name}
                type="button"
                onClick={() => onChooseVoice(persona)}
                className="flex-1 flex flex-col items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-4 transition hover:border-[#85d4b4] hover:bg-[#d4ede5]/30 hover:shadow-sm"
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-full text-white text-lg font-bold ${
                  persona.gender === 'female' ? 'bg-purple-500' : 'bg-sky-500'
                }`}>
                  {persona.name[0]}
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-[#0f0f0d]">{persona.name}</p>
                  <p className="text-[11px] text-[#7a7870]">{persona.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-black/10" />
          <span className="text-[11px] font-medium text-[#b8b5ae] uppercase">or</span>
          <div className="flex-1 h-px bg-black/10" />
        </div>

        {/* Type it yourself */}
        <button
          type="button"
          onClick={onChooseType}
          className="group flex items-center gap-3 rounded-xl border-2 border-black/10 bg-white px-4 py-3.5 text-left transition hover:border-[#85d4b4] hover:bg-[#d4ede5]/30"
        >
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#f3f0ea] group-hover:bg-[#d4ede5]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#7a7870] group-hover:text-[#1a4a3a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0f0f0d]">Type it yourself</p>
            <p className="text-xs text-[#7a7870]">Describe your ideal property in text</p>
          </div>
        </button>
      </div>
    </div>
  );
}
